// src/components/TrialBanner.jsx
// Banner showing trial days remaining with urgency-based styling

import React, { useMemo } from "react";
import { Box, Typography, Button, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";

/**
 * TrialBanner - Shows trial days remaining with urgency styling
 *
 * @param {Object} props
 * @param {string} props.trialEndsAt - ISO date string when trial ends
 */
export default function TrialBanner({ trialEndsAt }) {
  const theme = useTheme();
  const navigate = useNavigate();

  const daysLeft = useMemo(() => {
    if (!trialEndsAt) return 0;
    const endDate = new Date(trialEndsAt);
    const now = new Date();
    return Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
  }, [trialEndsAt]);

  // Determine urgency level and styling
  const { bgcolor, color, Icon, message } = useMemo(() => {
    if (daysLeft <= 1) {
      return {
        bgcolor: theme.palette.error.main,
        color: theme.palette.error.contrastText,
        Icon: ErrorOutlineRoundedIcon,
        message: daysLeft === 1 ? "Your trial ends tomorrow!" : "Your trial ends today!",
      };
    }
    if (daysLeft <= 3) {
      return {
        bgcolor: theme.palette.warning.main,
        color: theme.palette.warning.contrastText,
        Icon: WarningAmberRoundedIcon,
        message: `Your trial ends in ${daysLeft} days`,
      };
    }
    return {
      bgcolor: theme.palette.info.main,
      color: theme.palette.info.contrastText,
      Icon: AccessTimeRoundedIcon,
      message: `You have ${daysLeft} days left in your free trial`,
    };
  }, [daysLeft, theme]);

  if (daysLeft < 0) {
    return null;
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: 48,
        bgcolor,
        color,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        px: 2,
        position: "sticky",
        top: 0,
        zIndex: theme.zIndex.appBar + 1,
      }}
    >
      <Icon sx={{ fontSize: 20 }} />
      <Typography variant="body2" fontWeight={600}>
        {message}
      </Typography>
      <Button
        variant="contained"
        size="small"
        onClick={() => navigate("/pricing")}
        sx={{
          bgcolor: "rgba(255,255,255,0.9)",
          color: bgcolor,
          fontWeight: 700,
          fontSize: "0.75rem",
          py: 0.5,
          px: 2,
          "&:hover": {
            bgcolor: "rgba(255,255,255,1)",
          },
        }}
      >
        View Plans
      </Button>
    </Box>
  );
}
